import React, { useState, useCallback, useEffect } from 'react';
import api from '@/utils/api';
import { toastMessage } from '@/utils/toast-message';
import { RefreshCw, Download, Layers, Users, Activity, Ban } from 'lucide-react';
import { rrGetAgents } from '@/actions/rr';
import { InfoTip, ReportNotes } from '@/pages/reports/_Components/InfoTip';

interface Cohort {
  cohort_date: string;
  cohort_size: number;
  d1: number; d3: number; d5: number; d7: number; d10: number; d30: number;
  d1_pct: number; d3_pct: number; d5_pct: number; d7_pct: number; d10_pct: number; d30_pct: number;
  total_converted: number;
  age_days: number;
  status: 'tracking' | 'active' | 'retire' | 'complete';
}
interface Summary { total_cohorts: number; total_leads: number; avg_d10_pct: number; retire_candidates: number; }
interface Agent { user_id: number; name: string; }

const fmt = (n: number) => (n ?? 0).toLocaleString('en-IN');
const isoDaysAgo = (d: number) => {
  const t = new Date(); t.setDate(t.getDate() - d);
  return t.toISOString().slice(0, 10);
};

// Green intensity shading for a conversion %
const cellStyle = (pct: number): React.CSSProperties => {
  const a = Math.min(0.9, (pct / 100) * 0.95 + 0.05);
  return { backgroundColor: `rgba(22,163,74,${a})`, color: pct >= 45 ? '#fff' : '#14532d' };
};

const STATUS: Record<string, string> = {
  tracking: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  active: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  retire: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  complete: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
};

const StatCard = ({ icon, label, value, accent, info }: { icon: React.ReactNode; label: string; value: string; accent?: string; info?: string }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 flex items-center gap-3">
    <div className={`p-2.5 rounded-lg ${accent || 'bg-brandColor/10 text-brandColor'}`}>{icon}</div>
    <div className="min-w-0">
      <p className="text-xs text-gray-500 dark:text-gray-400 truncate flex items-center gap-1">
        {label}{info && <InfoTip text={info} />}
      </p>
      <p className="text-xl font-bold text-gray-900 dark:text-white leading-tight">{value}</p>
    </div>
  </div>
);

const DAYS: Array<{ key: keyof Cohort; pct: keyof Cohort; label: string }> = [
  { key: 'd1', pct: 'd1_pct', label: 'Day 1' },
  { key: 'd3', pct: 'd3_pct', label: 'Day 3' },
  { key: 'd5', pct: 'd5_pct', label: 'Day 5' },
  { key: 'd7', pct: 'd7_pct', label: 'Day 7' },
  { key: 'd10', pct: 'd10_pct', label: 'Day 10' },
];

const AbcCohortsReport: React.FC = () => {
  const today = new Date().toISOString().slice(0, 10);
  const [fromDate, setFromDate] = useState(isoDaysAgo(30));
  const [toDate, setToDate] = useState(today);
  const [agentId, setAgentId] = useState<number | ''>('');
  const [agents, setAgents] = useState<Agent[]>([]);
  const [rows, setRows] = useState<Cohort[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    rrGetAgents().then(res => setAgents((res?.data as Agent[]) || [])).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.req({
        url: '/reports/abc/cohorts',
        method: api.Method.POST,
        data: { from_date: fromDate, to_date: toDate, agent_user_id: agentId || undefined },
      }) as unknown as { data: Cohort[]; summary: Summary };
      setRows(res.data || []);
      setSummary(res.summary || null);
    } catch {
      toastMessage('Failed to load ABC cohort report', 'error');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, agentId]);

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadCsv = () => {
    const head = ['Cohort Date', 'Size', 'Day1 %', 'Day3 %', 'Day5 %', 'Day7 %', 'Day10 %', 'Total Converted', 'Age (days)', 'Status'];
    const lines = rows.map(r => [
      r.cohort_date, r.cohort_size, r.d1_pct, r.d3_pct, r.d5_pct, r.d7_pct, r.d10_pct, r.total_converted, r.age_days, r.status,
    ].join(','));
    const csv = [head.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `abc-cohorts_${fromDate}_${toDate}.csv`;
    a.click();
  };

  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-gray-900">
      {/* Header / filters */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center space-x-3">
            <div className="bg-brandColor p-2 rounded-lg"><Layers className="h-5 w-5 text-white" /></div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">ABC Cohort Tracking</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">Not-connected leads → % rescued (connected) within Day 1–10</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <input type="date" value={fromDate} max={toDate} onChange={e => setFromDate(e.target.value)}
              className="text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brandColor" />
            <input type="date" value={toDate} min={fromDate} onChange={e => setToDate(e.target.value)}
              className="text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brandColor" />
            <select value={agentId} onChange={e => setAgentId(e.target.value ? Number(e.target.value) : '')}
              className="text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brandColor max-w-[180px]">
              <option value="">All agents</option>
              {agents.map(a => <option key={a.user_id} value={a.user_id}>{a.name}</option>)}
            </select>
            <button onClick={fetchData} disabled={loading}
              className="flex items-center gap-2 px-4 py-1.5 bg-brandColor text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /><span>{loading ? 'Loading…' : 'Run'}</span>
            </button>
            <button onClick={downloadCsv} disabled={!rows.length}
              className="flex items-center gap-2 px-3 py-1.5 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
              <Download className="h-4 w-4" /><span>CSV</span>
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6 space-y-6">
        {/* KPI cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard icon={<Layers className="h-5 w-5" />} label="Cohorts" value={fmt(summary.total_cohorts)} info="Number of daily cohorts in range. A cohort is all leads first marked Not-Connected on the same day (IST)." />
            <StatCard icon={<Users className="h-5 w-5" />} label="Leads Tracked" value={fmt(summary.total_leads)} info="Total not-connected leads across all cohorts in the range." />
            <StatCard icon={<Activity className="h-5 w-5" />} label="Avg Day-10 Conversion" value={`${summary.avg_d10_pct}%`} accent="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" info="Average across cohorts of the % of not-connected leads that became Connected within 10 days of joining the cohort." />
            <StatCard icon={<Ban className="h-5 w-5" />} label="Retire Candidates" value={fmt(summary.retire_candidates)} accent="bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400" info="Leads 30+ days old with zero conversions — candidates to stop chasing." />
          </div>
        )}

        {/* Retention heatmap */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
              Cohort movement — % connected by day (darker = higher)
              <InfoTip text="Each row is a cohort (leads first marked Not-Connected that day). Each cell = cumulative % of that cohort that became Connected within N days of joining — so values only rise left to right. Darker green = higher recovery." />
            </h2>
            <span className="text-xs text-gray-400">cells show cumulative % rescued</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700/50 text-gray-500 dark:text-gray-400">
                <tr>
                  <th className="px-3 py-2.5 text-left font-medium whitespace-nowrap">Cohort</th>
                  <th className="px-3 py-2.5 text-left font-medium">Size</th>
                  {DAYS.map(d => <th key={d.label} className="px-3 py-2.5 text-center font-medium">{d.label}</th>)}
                  <th className="px-3 py-2.5 text-left font-medium">Age</th>
                  <th className="px-3 py-2.5 text-left font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {rows.map(r => (
                  <tr key={r.cohort_date} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                    <td className="px-3 py-2 font-medium text-gray-900 dark:text-white whitespace-nowrap">{r.cohort_date}</td>
                    <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{fmt(r.cohort_size)}</td>
                    {DAYS.map(d => {
                      const pct = r[d.pct] as number;
                      const cnt = r[d.key] as number;
                      return (
                        <td key={d.label} className="px-1 py-1 text-center">
                          <div className="rounded-md py-1.5 text-xs font-semibold" style={cellStyle(pct)} title={`${cnt} of ${r.cohort_size}`}>
                            {pct}%
                          </div>
                        </td>
                      );
                    })}
                    <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{r.age_days}d</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS[r.status]}`}>{r.status}</span>
                    </td>
                  </tr>
                ))}
                {!rows.length && !loading && (
                  <tr><td colSpan={9} className="px-3 py-10 text-center text-gray-400">No cohorts in this range.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* How to read this report */}
        <ReportNotes items={[
          { term: 'Cohort', def: 'All leads first marked Not-Connected on the same day (IST). The cohort date is that first not-connected day.' },
          { term: 'Day 1 / 3 / 5 / 7 / 10', def: 'Cumulative % of the cohort that became Connected within that many days of joining. Values only rise across the row.' },
          { term: 'Darker green', def: 'Higher recovery. Use it to spot at a glance which cohorts were rescued and which went cold.' },
          { term: 'Age', def: 'Days since the cohort formed. Determines how complete its tracking window is.' },
          { term: 'Status', def: 'tracking = under 10 days old (still filling in); active = under 30 days; complete = fully tracked; retire = 30+ days old with zero conversions (stop chasing).' },
        ]} />
      </div>
    </div>
  );
};

export default AbcCohortsReport;
